import { React, useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import { Stack, Modal, Box, Typography, Pagination, CircularProgress } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import Papa from "papaparse";
import MDBadge from "components/MDBadge";
import Select from "react-select";
import DescriptionIcon from "@mui/icons-material/Description";
import colors from "assets/theme/base/colors";
import "../campus/Campus.css";
import CancelIcon from "@mui/icons-material/Cancel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import nodata from "assets/images/nodata.png";
import { hasAdmissionPermission } from "hooks/permissions";
import { debounce } from "lodash";
const { info } = colors;
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

function TransferredApplications() {
  // State Declarations
  const [applications, setApplications] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [movementType, setMovementType] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const userDepartment = JSON.parse(sessionStorage.getItem("department_id"))
  // Multi-select filter states
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [quotaOptions, setQuotaOptions] = useState([]);
  
  // Selected filter values
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedCampuses, setSelectedCampuses] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedQuotas, setSelectedQuotas] = useState([]);
  const [isPaid, setIsPaid] = useState(null);
  const [isAdded, setIsAdded] = useState(null);

  // State Declarations for Edit Modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState(null);
  const [modalDepartmentId, setModalDepartmentId] = useState(null);
  const [modalProgramId, setModalProgramId] = useState(null);
  const [modalQuotaId, setModalQuotaId] = useState(null);
  const [modalDepartments, setModalDepartments] = useState([]);
  const [modalPrograms, setModalPrograms] = useState([]);
  const [modalQuotas, setModalQuotas] = useState([]);

  // Cancel Admission Dialog State
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelAppId, setCancelAppId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setPage(1);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearchHandler(searchKeyword);
    return () => debouncedSearchHandler.cancel();
  }, [searchKeyword, debouncedSearchHandler]);

  useEffect(() => {
    const fetchInit = async () => {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
  
      // Fetch campaigns, campuses, quotas
      const [campRes, campusRes, quotaRes] = await Promise.all([
        fetch(`${BASE_URL}/campaign/get_campaigns`, { headers }),
        fetch(`${BASE_URL}/campus/get_campuses`, { headers }),
        fetch(`${BASE_URL}/quota/get_quotas`, { headers }),
      ]);
      
      const campData = await campRes.json();
      const campusData = await campusRes.json();
      const quotaData = await quotaRes.json();
  
      const campaignOpts = (campData.campaigns || []).map(c => ({
        value: c.id,
        label: c.name,
        status: c.status, // <-- keep this!
      }));
      const campusOpts = (campusData.campuses || []).map(camp => ({ value: camp.id, label: camp.name }));
      const quotaOpts = (quotaData.quotas || []).map(q => ({ value: q.id, label: q.name }));
      
      setCampaignOptions(campaignOpts);
      setCampusOptions(campusOpts);
      setQuotaOptions(quotaOpts);

     // ✅ Use campaignOpts instead of campaignOptions
const active = campaignOpts.find((camp) => camp.status == 1);
if (active) {
  setSelectedCampaigns([active]);
}
      if (userDepartment && campusOpts.length > 0) {
        const firstCampus = campusOpts[0];
        setSelectedCampuses([firstCampus]);
  
        const deptRes = await fetch(`${BASE_URL}/department/get_departments_by_campuses`, {
          method: "POST",
          headers,
          body: JSON.stringify({ campus_ids: [firstCampus.value] })
        });
        const deptData = await deptRes.json();
        const deptOpts = (deptData.departments || []).map(d => ({ value: d.id, label: d.name }));
        setDepartmentOptions(deptOpts);
  
        if (deptOpts.length > 0) {
          setSelectedDepartments([deptOpts[0]]);
        }
      }
    };
  
    fetchInit();
  }, []);

  useEffect(() => {
    if (!hasFetched) {
      return;
    }
    getFilteredApplications(page, true);
  }, [page, debouncedSearch]);
  

  // Load dependent options when selections change
  useEffect(() => {
    if (selectedCampuses.length > 0) {
      getDepartmentsByCampuses(selectedCampuses.map(item => item.value));
    } else {
      setDepartmentOptions([]);
      setSelectedDepartments([]);
    }
  }, [selectedCampuses]);

  useEffect(() => {
    if (selectedDepartments.length > 0) {
      getProgramsByDepartments(selectedDepartments.map(item => item.value));
    } else {
      setProgramOptions([]);
      setSelectedPrograms([]);
    }
  }, [selectedDepartments]);

  // Modal department/program dependency
  useEffect(() => {
    if (openModal) {
      getModalDepartments();
      getModalQuotas();
    }
  }, [openModal]);

  useEffect(() => {
    if (modalDepartmentId) {
      getModalPrograms(modalDepartmentId);
    } else {
      setModalPrograms([]);
    }
  }, [modalDepartmentId]);

  const getAllCampaigns = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/campaign/get_campaigns`, requestOptions);
      const data = await response.json();
      if (data.campaigns) {
        const options = data.campaigns.map(camp => ({
          value: camp.id,
          label: camp.name
        }));
        setCampaignOptions(options);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const getAllCampuses = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/campus/get_campuses`, requestOptions);
      const data = await response.json();
      if (data.campuses) {
        const options = data.campuses.map(campus => ({
          value: campus.id,
          label: campus.name
        }));
        setCampusOptions(options);
      }
    } catch (error) {
      console.error("Error fetching campuses:", error);
    }
  };

  const getAllQuotas = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/quota/get_quotas`, requestOptions);
      const data = await response.json();
      if (data.quotas) {
        const options = data.quotas.map(quota => ({
          value: quota.id,
          label: quota.name
        }));
        setQuotaOptions(options);
      }
    } catch (error) {
      console.error("Error fetching quotas:", error);
    }
  };

  const getDepartmentsByCampuses = async (campusIds) => {
    const data = {
      campus_ids: campusIds
    };
    
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    };
    
    try {
      const response = await fetch(`${BASE_URL}/department/get_departments_by_campuses`, requestOptions);
      const data = await response.json();
      if (data.departments) {
        const options = data.departments.map(dept => ({
          value: dept.id,
          label: dept.name
        }));
        setDepartmentOptions(options);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const getProgramsByDepartments = async (departmentIds) => {
    const promises = departmentIds.map(depId => {
      return fetch(`${BASE_URL}/degree/get_degrees_by_department/${depId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    });
    
    try {
      const responses = await Promise.all(promises);
      const dataArray = await Promise.all(responses.map(response => response.json()));
      
      // Combine and deduplicate programs
      const allPrograms = [];
      dataArray.forEach(data => {
        if (data.degrees) {
          data.degrees.forEach(prog => {
            if (!allPrograms.some(p => p.value === prog.id)) {
              allPrograms.push({
                value: prog.id,
                label: prog.name
              });
            }
          });
        }
      });
      
      setProgramOptions(allPrograms);
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const getFilteredApplications = async (pageOverride = page, silent = false) => {
    const currentPage = pageOverride || 1;
    const skip = (currentPage - 1) * limit;
    const data = {
      campaign_ids: selectedCampaigns.map(item => item.value),
      campus_ids: selectedCampuses.map(item => item.value),
      department_ids: selectedDepartments.map(item => item.value),
      program_ids: selectedPrograms.map(item => item.value),
      quota_ids: selectedQuotas.map(item => item.value),
      is_paid: isPaid,
      is_added: isAdded,
      movement_type: movementType ? movementType.value : null,
      search: debouncedSearch.trim() || null,
      skip,
      limit,
    };

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    };

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/application/get_filtered_pre_register_applicants_transferred`, requestOptions);
      const responseData = await response.json();
      
      if (response.ok && responseData.selected_applicants) {
        setApplications(responseData.selected_applicants);
        setTotalCount(responseData.total_count || 0);
        setHasFetched(true);
        if (!silent) {
          toast.success(`Found ${responseData.total_count || 0} applicants`);
        }
      } else {
        setApplications([]);
        setTotalCount(0);
        setHasFetched(true);
        if (!silent) {
          toast.info("No applications found matching your criteria");
        }
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Error fetching applications");
      setApplications([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleGetApplications = () => {
    setPage(1);
    getFilteredApplications(1, false);
  };

  const getModalDepartments = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/department/get_departments`, requestOptions);
      const data = await response.json();
      if (data.departments) {
        setModalDepartments(data.departments);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const getModalPrograms = async (departmentId) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/degree/get_degrees_by_department/${departmentId}`, requestOptions);
      const data = await response.json();
      if (data.degrees) {
        setModalPrograms(data.degrees);
      } else {
        setModalPrograms([]);
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const getModalQuotas = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/quota/get_quotas`, requestOptions);
      const data = await response.json();
      if (data.quotas) {
        setModalQuotas(data.quotas);
      }
    } catch (error) {
      console.error("Error fetching quotas:", error);
    }
  };

  const handleOpenModal = (appId) => {
    setSelectedApplicantId(appId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalDepartmentId(null);
    setModalProgramId(null);
    setModalQuotaId(null);
  };

  const handleSaveChanges = async () => {
    const data = {
      selected_applicant_id: selectedApplicantId,
      department_id: modalDepartmentId,
      program_id: modalProgramId,
      quota_id: modalQuotaId,
    };

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    };

    try {
      const response = await fetch(`${BASE_URL}/application/change_selection`, requestOptions);
      if (response.ok) {
        getFilteredApplications();
        handleCloseModal();
        toast.success("Changes saved successfully");
      } else {
        toast.error("Failed to save changes");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Error saving changes");
    }
  };

  const handleSearchKeywordChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleVoucherStatusChange = (option) => {
    if (!option) {
      setIsPaid(null);
      return;
    }
    
    if (option.value === 'all') {
      setIsPaid(null);
    } else if (option.value === 'paid') {
      setIsPaid(true);
    } else if (option.value === 'not_paid') {
      setIsPaid(false);
    }
  };

  const handleAddedStatusChange = (option) => {
    if (!option) {
      setIsAdded(null);
      return;
    }
    
    if (option.value === 'all') {
      setIsAdded(null);
    } else if (option.value === 'added') {
      setIsAdded(true);
    } else if (option.value === 'not_added') {
      setIsAdded(false);
    }
  };

  const handleExportCSV = async () => {
    toast.info("Preparing export... Please wait.");
    const data = {
      campaign_ids: selectedCampaigns.map(item => item.value),
      campus_ids: selectedCampuses.map(item => item.value),
      department_ids: selectedDepartments.map(item => item.value),
      program_ids: selectedPrograms.map(item => item.value),
      quota_ids: selectedQuotas.map(item => item.value),
      is_paid: isPaid,
      is_added: isAdded,
      movement_type: movementType ? movementType.value : null,
      search: debouncedSearch.trim() || null,
      export_all: true,
    };

    try {
      const response = await fetch(`${BASE_URL}/application/get_filtered_pre_register_applicants_transferred`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      const responseData = await response.json();
      const allApplicants = responseData.selected_applicants || [];
      if (!allApplicants.length) {
        toast.error("No data to export");
        return;
      }
      const exportData = allApplicants.map((app, index) => ({
        "Sr.No": index + 1,
        "Application ID": app.id,
        "Name": app.name || "",
        "Father Name": app.father_name || "",
        "Gender": app.gender || "",
        "Phone": app.phone || "",
        "Type": app.movement_type || "",
        "From Department": app.from_department_name || "",
        "From Program": app.from_program_name || "",
        "To Department": app.to_department_name || app.department_name || "",
        "To Program": app.to_program_name || app.program_name || "",
        "Date": app.movement_date || "",
        "Fee Status": app.voucher_status ? "Paid" : "Not Paid",
        "Quota": app.quota_name || "",
      }));

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Transferred_Shifted_Applicants_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      toast.success(`Successfully exported ${allApplicants.length} applicants!`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Error exporting CSV");
    }
  };

  // Cancel Admission Handler
  const handleOpenCancelDialog = (appId) => {
    setCancelAppId(appId);
    setCancelDialogOpen(true);
  };
  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelAppId(null);
  };
  const handleConfirmCancelAdmission = async () => {
    setCancelLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/application/admission_cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ application_id: cancelAppId }),
      });
      if (response.ok) {
        toast.success("Admission cancelled successfully");
        getFilteredApplications();
      } else {
        toast.error("Failed to cancel admission");
      }
    } catch (error) {
      toast.error("Error cancelling admission");
    }
    setCancelLoading(false);
    handleCloseCancelDialog();
  };

  // Rows & Columns for DataTable
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Application ID", accessor: "application_id", align: "center" },
    { Header: "Name", accessor: "first_name", align: "center" },
    { Header: "Father Name", accessor: "father_name", align: "center" },
    { Header: "Type", accessor: "movement_type", align: "center" },
    { Header: "From Department", accessor: "from_department", align: "center" },
    { Header: "From Program", accessor: "from_program", align: "center" },
    { Header: "To Department", accessor: "to_department", align: "center" },
    { Header: "To Program", accessor: "to_program", align: "center" },
    { Header: "Date", accessor: "movement_date", align: "center" },
    { Header: "Fee Status", accessor: "voucher_status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  const rows = applications.map((app, index) => ({
    serial: (page - 1) * limit + index + 1,
    application_id: app.id,
    first_name: app.name,
    father_name: app.father_name,
    movement_type: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={app.movement_type || "N/A"}
          color={app.movement_type === "Transferred" ? "info" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    from_department: app.from_department_name || "N/A",
    from_program: app.from_program_name || "N/A",
    to_department: app.to_department_name || app.department_name || "N/A",
    to_program: app.to_program_name || app.program_name || "N/A",
    movement_date: app.movement_date || "N/A",
    voucher_status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={app.voucher_status ? "Paid" : "Not Paid"}
          color={app.voucher_status ? "success" : "error"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    actions: (
      <>
        {hasAdmissionPermission("TransferredApplications", "READ") && (
        <IconButton onClick={() => handleViewApplication(app.id)}>
          <VisibilityIcon style={{ color: "blue" }} />
        </IconButton>
        )}
      </>
    ),
  }));

  const handleViewApplication = (id) => {
    window.open(`/admissions/get-applications/${id}`);
  };

  // Select component styling
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      borderRadius: '8px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };
  
  // Voucher status options
  const voucherStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'paid', label: 'Paid' },
    { value: 'not_paid', label: 'Not Paid' }
  ];

  // Added status options
  const addedStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'added', label: 'Added' },
    { value: 'not_added', label: 'Not Added' }
  ];

  const movementTypeOptions = [
    { value: 'Transferred', label: 'Transferred' },
    { value: 'Shifted', label: 'Shifted' },
  ];

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <Grid spacing={6}>
          <Grid item xs={12}>
            <MDTypography variant="h4" fontWeight="medium" mb={2}>
              Filter Transferred / Shifted Applicants
            </MDTypography>
            
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">Campaign</MDTypography>
                <Select
                  isMulti
                  name="campaigns"
                  options={campaignOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Campaigns"
                  value={selectedCampaigns}
                  onChange={setSelectedCampaigns}
                  styles={customStyles}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">Campus</MDTypography>
                <Select
                  isMulti
                  name="campuses"
                  options={campusOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Campuses"
                  value={selectedCampuses}
                  onChange={setSelectedCampuses}
                  styles={customStyles}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="bold">Department</MDTypography>
                <Select
                  isMulti
                  name="departments"
                  options={departmentOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Departments"
                  value={selectedDepartments}
                  onChange={setSelectedDepartments}
                  styles={customStyles}
                  isDisabled={!selectedCampuses.length}
                />
              </Grid>
            </Grid>
            
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">Program</MDTypography>
                <Select
                  isMulti
                  name="programs"
                  options={programOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Programs"
                  value={selectedPrograms}
                  onChange={setSelectedPrograms}
                  styles={customStyles}
                  isDisabled={!selectedDepartments.length}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">Quota</MDTypography>
                <Select
                  isMulti
                  name="quotas"
                  options={quotaOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Quotas"
                  value={selectedQuotas}
                  onChange={setSelectedQuotas}
                  styles={customStyles}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">Type</MDTypography>
                <Select
                  name="movementType"
                  options={movementTypeOptions}
                  className="basic-select"
                  classNamePrefix="select"
                  placeholder="Transferred & Shifted"
                  value={movementType}
                  onChange={setMovementType}
                  isClearable
                  styles={customStyles}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">Voucher Status</MDTypography>
                <Select
                  name="voucherStatus"
                  options={voucherStatusOptions}
                  className="basic-select"
                  classNamePrefix="select"
                  placeholder="Select Voucher Status"
                  onChange={handleVoucherStatusChange}
                  isClearable
                  styles={customStyles}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography variant="caption" fontWeight="bold">Added Status</MDTypography>
                <Select
                  name="addedStatus"
                  options={addedStatusOptions}
                  className="basic-select"
                  classNamePrefix="select"
                  placeholder="Select Added Status"
                  onChange={handleAddedStatusChange}
                  isClearable
                  styles={customStyles}
                />
              </Grid>
            </Grid>
            
            <Grid container justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                endIcon={<SearchIcon />}
                onClick={handleGetApplications}
                sx={{ mt: 2 }}
                style={{color: "white"}}
              >
                Get Applications
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card className="card1">
              <MDBox mx={0} mt={0} py={2} px={2} display="flex" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                  <Paper component="form" className="search-paper" sx={{ flex: 1 }}>
                    <InputBase
                      sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                      placeholder="Search"
                      inputProps={{ "aria-label": "search applications" }}
                      value={searchKeyword}
                      onChange={handleSearchKeywordChange}
                    />
                    <SearchIcon className="search-icon" />
                  </Paper>
                  {hasAdmissionPermission('TransferredApplications','EXPORT CSV') && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    style={{color: "white"}}
                    endIcon={<DescriptionIcon />}
                    onClick={handleExportCSV}
                  >
                    Export CSV
                  </Button>
                  )}
                </Stack>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                  </Box>
                ) : applications && applications.length > 0 ? (
                  <>
                    <MDTypography variant="caption" px={2}>
                      Showing {(page - 1) * limit + 1}-{Math.min(page * limit, totalCount)} of {totalCount}
                    </MDTypography>
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                    {totalPages > 1 && (
                      <Box display="flex" justifyContent="center" py={2}>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          disabled={loading}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "30px" }}>
                    <img
                      src={nodata}
                      style={{
                        display: "block",
                        margin: "auto",
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      No applications found. Try adjusting your filters.
                    </MDTypography>
                  </div>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit Program Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "750px",
            bgcolor: "background.paper",
            borderRadius: "6px",
            p: 5,
          }}
        >
          <Typography variant="h4" sx={{ my: 2, fontWeight: "bold" }}>
            Edit Program Selection
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDTypography variant="caption" fontWeight="bold">Department</MDTypography>
              <Select
                name="modalDepartment"
                options={modalDepartments.map(dep => ({
                  value: dep.id,
                  label: dep.name
                }))}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Select Department"
                onChange={(option) => setModalDepartmentId(option ? option.value : null)}
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="caption" fontWeight="bold">Program</MDTypography>
              <Select
                name="modalProgram"
                options={modalPrograms.map(prog => ({
                  value: prog.id,
                  label: prog.name
                }))}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Select Program"
                onChange={(option) => setModalProgramId(option ? option.value : null)}
                isDisabled={!modalDepartmentId}
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={6} sx={{ mt: 2 }}>
              <MDTypography variant="caption" fontWeight="bold">Quota</MDTypography>
              <Select
                name="modalQuota"
                options={modalQuotas.map(quota => ({
                  value: quota.id,
                  label: quota.name
                }))}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Select Quota"
                onChange={(option) => setModalQuotaId(option ? option.value : null)}
                styles={customStyles}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "35px",
            }}
          >
            <Button
              onClick={handleSaveChanges}
              className="dialog-actions-button positive"
              variant="contained"
              color="primary"
            >
              Save
            </Button>
            <Button
              onClick={handleCloseModal}
              className="dialog-actions-button negative"
              sx={{ marginLeft: "10px" }}
              variant="outlined"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Cancel Admission Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog}>
        <DialogTitle>Cancel Admission</DialogTitle>
        <DialogContent>
          Are you sure you want to cancel this admission?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={cancelLoading}>No</Button>
          <Button onClick={handleConfirmCancelAdmission} color="error" disabled={cancelLoading}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </DashboardLayout>
  );
}

export default TransferredApplications;